import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { UploadedReadedFile } from 'shared/lib/components/controls/uploadArea/uploadArea';
import { UiContext } from 'shared/lib/contexts/uiContext';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { UploadedFileOrString } from 'shared/lib/utils/uploadedFileOrString';
import UploadImageControl from 'shared/lib/components/controls/uploadImageControl';
import { useTranslation } from 'react-i18next';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';

const SECTION_NAME_MAX_LENGTH = 100;

export type EditSectionApplyParams = {
  name: string;
  image: UploadedFileOrString;
};

export default observer(function EditSectionModal({
  isOpen,
  handleClose,
  onSubmit,
  onDelete,
  currentParams,
}: {
  onSubmit: (params: EditSectionApplyParams) => Promise<any>;
  currentParams?: EditSectionApplyParams;
  onDelete?: () => void;
} & ModalProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const [isApplyLoading, setIsApplyLoading] = useState(false);

  const [imageError, setImageError] = useState(false);
  const [image, setImage] = useState(
    currentParams ? currentParams.image : null
  );
  const handleAddFile = useCallback((file: UploadedReadedFile) => {
    setImageError(false);
    setImage(new UploadedFileOrString(file));
  }, []);
  const handleRemoveImage = useCallback(() => setImage(null), []);

  const validationSchema = yup.object({
    sectionName: yup
      .string()
      .required(t('errors.validationSchema.required'))
      .max(
        SECTION_NAME_MAX_LENGTH,
        t('errors.validationSchema.titleTooLong', {
          maxLength: SECTION_NAME_MAX_LENGTH,
        })
      ),
  });

  const [formikSubmited, setFormikSubmited] = useState(false);
  const formik = useFormik({
    initialValues: {
      sectionName: currentParams?.name || '',
      image,
    },
    validateOnBlur: false,
    validateOnChange: formikSubmited,
    validationSchema: validationSchema,
    onSubmit: async ({ sectionName }) => {
      if (!image) {
        setImageError(true);
        return;
      }

      setImageError(false);

      setIsApplyLoading(true);
      await onSubmit({
        name: sectionName,
        image: image,
      });
      setIsApplyLoading(false);
    },
  });

  const isDisabled = useMemo(() => {
    const changes =
      formik.values.sectionName !== currentParams?.name ||
      image?.src !== currentParams?.image.src;
    return !changes;
  }, [formik.values.sectionName, image, currentParams]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Typography variant="f24">
        {currentParams
          ? t('actions.editing.section')
          : t('actions.creating.section')}
      </Typography>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(sp.s50),
        }}
        onSubmit={formik.handleSubmit}
      >
        <CustomTextField
          wrapperSxProps={{ width: 1 }}
          id="sectionName"
          name="sectionName"
          label={t('formFields.titleOfSection')}
          placeholder={t('formFields.addTitle')}
          value={formik.values.sectionName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.sectionName && !!formik.errors.sectionName}
          helperText={formik.touched.sectionName && formik.errors.sectionName}
          autoFocus={true}
          isClearIcon={true}
        />
        <UploadImageControl
          image={image}
          onRemove={handleRemoveImage}
          onUpload={handleAddFile}
          error={imageError}
          isCroppDisabled={true}
          accept={{
            'image/svg': ['.svg'],
          }}
        ></UploadImageControl>

        <ButtonsContainer variant="vertical" sx={{ width: 1 }}>
          <ButtonsContainer>
            <SecondaryButton variant="outlined" onClick={handleClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton
              disabled={isDisabled}
              onClick={() => {
                setFormikSubmited(true);
              }}
              type="submit"
              loading={isApplyLoading}
            >
              {currentParams ? t('common.save') : t('common.create')}
            </PrimaryButton>
          </ButtonsContainer>

          {onDelete ? (
            <PrimaryButton onClick={() => onDelete()} color="error">
              {t('common.delete')}
            </PrimaryButton>
          ) : (
            ''
          )}
        </ButtonsContainer>
      </form>
    </Modal>
  );
});
